// Lost Lands 2026: days, stages, the walk matrix, the acts and their set times.
//
// All five days carry sets, transcribed from the published set-time graphics.
//
// Two values here are estimates rather than transcription. End times: the
// graphics publish starts only, so a set runs until the next one on its own stage
// and the last of each stage takes the length in `minutes`. Walk times: no data
// source exists, so they run five to ten minutes stage to stage and twelve to
// fifteen back to camp, and they are editable on the import screen.
//
// Genre tags decide which acts a subgenre answer warms up before any real rating
// exists, and nothing else.

import type {
  Artist,
  Day,
  Festival,
  FestivalSet,
  Stage,
  WalkMatrix,
} from '../engine/types.ts';
import { VENUE_TIMEZONE, festivalTimestamp } from '../engine/time.ts';

export const LINEUP_VERSION = 'lost-lands-2026-r2';

export const SUBGENRES = [
  { tag: 'riddim', label: 'Riddim' },
  { tag: 'tearout', label: 'Tearout' },
  { tag: 'melodic', label: 'Melodic dubstep' },
  { tag: 'dnb', label: 'Drum and bass' },
  { tag: 'trap', label: 'Trap' },
  { tag: 'experimental', label: 'Experimental and halftime' },
  { tag: 'house', label: 'House and tech' },
  { tag: 'hard', label: 'Hardstyle and hard techno' },
];

export const FESTIVAL: Festival = {
  id: 'lost-lands-2026',
  name: 'Lost Lands 2026',
  timezone: 'America/New_York',
  startDate: '2026-09-16',
  endDate: '2026-09-20',
  source: 'published set-time graphics, Wednesday through Sunday',
  ingestedAt: '2026-09-09T00:00:00.000Z',
};

export const DAYS: Day[] = [
  { id: 'day-wed', festivalId: FESTIVAL.id, label: 'Wednesday', calendarDate: '2026-09-16', rolloverHour: 6 },
  { id: 'day-thu', festivalId: FESTIVAL.id, label: 'Thursday', calendarDate: '2026-09-17', rolloverHour: 6 },
  { id: 'day-fri', festivalId: FESTIVAL.id, label: 'Friday', calendarDate: '2026-09-18', rolloverHour: 6 },
  { id: 'day-sat', festivalId: FESTIVAL.id, label: 'Saturday', calendarDate: '2026-09-19', rolloverHour: 6 },
  { id: 'day-sun', festivalId: FESTIVAL.id, label: 'Sunday', calendarDate: '2026-09-20', rolloverHour: 6 },
];

export const STAGES: Stage[] = [
  { id: 'stage-prehistoric', festivalId: FESTIVAL.id, name: 'Prehistoric Stage', capacityTier: 'main' },
  { id: 'stage-wompy', festivalId: FESTIVAL.id, name: 'Wompy Woods', capacityTier: 'large' },
  { id: 'stage-crater', festivalId: FESTIVAL.id, name: 'The Crater', capacityTier: 'large' },
  { id: 'stage-forest', festivalId: FESTIVAL.id, name: 'Forest Stage', capacityTier: 'medium' },
  { id: 'stage-subsidia', festivalId: FESTIVAL.id, name: 'Subsidia Stage', capacityTier: 'medium' },
  { id: 'stage-raptor', festivalId: FESTIVAL.id, name: 'Raptor Alley', capacityTier: 'small' },
  { id: 'stage-grove', festivalId: FESTIVAL.id, name: 'Grove Stage', capacityTier: 'medium' },
  { id: 'stage-camp', festivalId: FESTIVAL.id, name: 'Camp', capacityTier: 'non_music', isNonMusic: true },
];

export const HOME_STAGE = 'stage-camp';

export const WALK: WalkMatrix = {
  festivalId: FESTIVAL.id,
  defaultMinutes: 8,
  minutes: {
    'stage-prehistoric|stage-wompy': 6,
    'stage-crater|stage-prehistoric': 7,
    'stage-forest|stage-prehistoric': 9,
    'stage-prehistoric|stage-subsidia': 8,
    'stage-prehistoric|stage-raptor': 10,
    'stage-grove|stage-prehistoric': 8,
    'stage-camp|stage-prehistoric': 14,

    'stage-crater|stage-wompy': 5,
    'stage-forest|stage-wompy': 8,
    'stage-subsidia|stage-wompy': 9,
    'stage-raptor|stage-wompy': 10,
    'stage-grove|stage-wompy': 9,
    'stage-camp|stage-wompy': 13,

    'stage-crater|stage-forest': 7,
    'stage-crater|stage-subsidia': 6,
    'stage-crater|stage-raptor': 8,
    'stage-crater|stage-grove': 5,
    'stage-camp|stage-crater': 15,

    'stage-forest|stage-subsidia': 5,
    'stage-forest|stage-raptor': 7,
    'stage-forest|stage-grove': 10,
    'stage-camp|stage-forest': 12,

    'stage-raptor|stage-subsidia': 5,
    'stage-grove|stage-subsidia': 9,
    'stage-camp|stage-subsidia': 13,

    'stage-grove|stage-raptor': 10,
    'stage-camp|stage-raptor': 12,

    'stage-camp|stage-grove': 14,
  },
};

interface Slot {
  /** Local 24-hour clock. An hour below the rollover belongs to the night before. */
  at: [number, number];
  act: string;
  tags: string[];
  note?: string;
  /** Length in minutes, for the last set on a stage or a printed duration. */
  minutes?: number;
}

type StageDay = Record<string, Slot[]>;

const FRIDAY: StageDay = {
  'stage-prehistoric': [
    { at: [14, 0], act: 'Tynan', tags: ['riddim', 'tearout'] },
    { at: [15, 0], act: 'Seth David', tags: ['riddim'] },
    { at: [16, 0], act: 'Jkyl & Hyde', tags: ['riddim', 'tearout'] },
    { at: [17, 0], act: 'Ravenscoon', tags: ['melodic', 'experimental'] },
    { at: [18, 0], act: 'Infekt b2b Samplifire', tags: ['tearout'], note: 'b2b' },
    { at: [19, 0], act: 'Hol!', tags: ['experimental'] },
    { at: [20, 0], act: 'Sullivan King b2b Ray Volpe', tags: ['tearout', 'hard'], note: 'b2b' },
    { at: [21, 0], act: 'Levity', tags: ['melodic', 'experimental'] },
    { at: [22, 10], act: 'Excision', tags: ['tearout', 'riddim'], note: 'two hour set', minutes: 120 },
  ],
  'stage-wompy': [
    { at: [14, 30], act: 'Paper Skies', tags: ['melodic'] },
    { at: [15, 30], act: 'VKTM', tags: ['riddim'] },
    { at: [16, 30], act: 'Benda', tags: ['dnb'] },
    { at: [17, 30], act: 'Drinkurwater', tags: ['experimental', 'trap'] },
    { at: [18, 30], act: 'Dion Timmer', tags: ['melodic', 'tearout'] },
    { at: [19, 30], act: 'Wooli', tags: ['melodic'], note: 'sunset set' },
    { at: [20, 30], act: 'Liquid Stranger', tags: ['experimental'] },
    { at: [21, 30], act: 'NGHTMRE', tags: ['trap', 'tearout'] },
    { at: [22, 45], act: 'The Resistance', tags: ['tearout'] },
    { at: [0, 15], act: 'Sippy', tags: ['riddim', 'tearout'] },
    { at: [1, 10], act: 'Yookie', tags: ['tearout', 'trap'] },
    { at: [2, 5], act: 'Riot', tags: ['dnb', 'tearout'] },
    { at: [3, 0], act: 'Oliverse', tags: ['melodic', 'dnb'], minutes: 60 },
  ],
  'stage-crater': [
    { at: [14, 0], act: 'Shank Aaron', tags: ['riddim'] },
    { at: [15, 15], act: 'Crizzly', tags: ['trap'] },
    { at: [16, 25], act: 'Badklaat', tags: ['riddim'] },
    { at: [17, 30], act: 'The Frim', tags: ['riddim'] },
    { at: [18, 35], act: 'Bear Grillz', tags: ['tearout'] },
    { at: [19, 40], act: 'Doctor P', tags: ['tearout'] },
    { at: [20, 45], act: 'Borgore', tags: ['trap'] },
    { at: [21, 50], act: 'Dirtysnatcha', tags: ['riddim', 'tearout'] },
    { at: [22, 55], act: 'The Widdler', tags: ['experimental'] },
    { at: [0, 0], act: 'Secret Takeover (midnight)', tags: [] },
    { at: [1, 0], act: 'Secret Takeover (1am)', tags: [] },
    { at: [2, 0], act: 'Secret Takeover (2am)', tags: [] },
    { at: [3, 0], act: 'Secret Takeover (3am)', tags: [], minutes: 60 },
  ],
  'stage-forest': [
    { at: [16, 0], act: 'Dr. Ushuu', tags: ['experimental'] },
    { at: [17, 0], act: 'Austeria', tags: ['experimental'] },
    { at: [18, 0], act: 'Probcause', tags: ['trap', 'experimental'] },
    { at: [19, 0], act: 'Canabliss', tags: ['experimental'] },
    { at: [20, 0], act: 'Ivy Lab', tags: ['dnb', 'experimental'] },
    { at: [21, 0], act: 'Jantsen', tags: ['experimental', 'trap'] },
    { at: [22, 0], act: 'Xotix', tags: ['riddim'] },
    { at: [23, 0], act: 'Shlump', tags: ['experimental'] },
    { at: [0, 0], act: 'Richard Finger', tags: ['experimental'] },
    { at: [1, 0], act: 'Lumasi', tags: ['experimental'] },
    { at: [2, 0], act: 'Wiley', tags: ['experimental'] },
    { at: [3, 0], act: 'Twopercent', tags: ['experimental'], minutes: 60 },
  ],
  'stage-subsidia': [
    { at: [15, 0], act: 'Lazrus', tags: ['riddim'] },
    { at: [16, 0], act: 'Poni', tags: ['riddim'] },
    { at: [17, 0], act: '$J', tags: ['riddim'] },
    { at: [18, 0], act: 'Future Exit', tags: ['melodic'] },
    { at: [19, 0], act: 'Dubscribe', tags: ['tearout'] },
    { at: [20, 0], act: 'Kliptic', tags: ['riddim'] },
    { at: [21, 0], act: 'Nimda', tags: ['tearout'] },
    { at: [22, 0], act: 'Vampa', tags: ['riddim'] },
    { at: [23, 0], act: 'Izadi', tags: ['riddim'] },
    { at: [0, 0], act: 'Zero', tags: ['riddim'] },
    { at: [1, 0], act: 'Klo', tags: ['tearout'] },
    { at: [2, 0], act: 'Casey Club', tags: ['house'] },
    { at: [3, 0], act: 'Neumonic', tags: ['tearout'], minutes: 60 },
  ],
  'stage-raptor': [
    { at: [0, 0], act: 'Funtcase b2b Doctor P', tags: ['dnb'], note: 'dnb set' },
    { at: [1, 0], act: 'Sigma', tags: ['dnb'] },
    { at: [2, 0], act: 'Subsonic', tags: ['dnb'] },
    { at: [3, 0], act: 'Basstripper', tags: ['dnb'], minutes: 60 },
  ],
};

const WEDNESDAY: StageDay = {
  'stage-grove': [
    { at: [13, 0], act: 'Hevnfall', tags: ['melodic'], note: 'fresh meat' },
    { at: [14, 0], act: 'Mport', tags: ['tearout'] },
    { at: [15, 0], act: 'Gardella', tags: ['riddim'] },
    { at: [15, 50], act: 'Hydraulix', tags: ['trap', 'tearout'] },
    { at: [16, 40], act: 'Hershe', tags: ['house'] },
    { at: [17, 30], act: 'Jaenga', tags: ['dnb'] },
    { at: [18, 20], act: 'Nikita, The Wicked', tags: ['tearout'] },
    { at: [19, 10], act: 'Smoakland', tags: ['experimental', 'house'] },
    { at: [20, 0], act: 'Hairitage', tags: ['trap', 'tearout'] },
    { at: [20, 50], act: 'Distinct Motive', tags: ['experimental'] },
    { at: [21, 40], act: 'Caspa', tags: ['experimental'], minutes: 50 },
  ],
  'stage-crater': [
    { at: [13, 0], act: 'Negat1ve5pace', tags: ['riddim'], note: 'fresh meat' },
    { at: [14, 0], act: 'Mile32', tags: ['riddim'] },
    { at: [15, 0], act: 'Chassi', tags: ['melodic'] },
    { at: [16, 0], act: 'Izzy Vadim', tags: ['riddim'] },
    { at: [17, 0], act: 'Muerte', tags: ['riddim'] },
    { at: [18, 0], act: 'Emorfik b2b Usaybflow', tags: ['riddim'], note: 'b2b' },
    { at: [19, 0], act: 'Calcium b2b Mad Dubz', tags: ['riddim', 'tearout'], note: 'b2b' },
    { at: [20, 0], act: 'Riot Ten', tags: ['tearout', 'trap'] },
    { at: [21, 0], act: 'Barely Alive', tags: ['tearout'], minutes: 60 },
  ],
};

const THURSDAY: StageDay = {
  'stage-grove': [
    { at: [13, 0], act: 'Mindset', tags: ['dnb'] },
    { at: [14, 0], act: 'Zen Selekta', tags: ['dnb'] },
    { at: [15, 0], act: 'Rsun', tags: ['melodic'] },
    { at: [16, 0], act: 'Phrva', tags: ['experimental'] },
    { at: [17, 0], act: 'Super Future', tags: ['experimental', 'melodic'] },
    { at: [18, 0], act: 'Dirt Monkey', tags: ['tearout', 'experimental'], minutes: 60 },
  ],
  'stage-crater': [
    { at: [13, 0], act: 'Keeb', tags: ['riddim'], note: 'fresh meat' },
    { at: [14, 0], act: 'Deadcrow', tags: ['experimental'] },
    { at: [15, 0], act: 'Otsukare', tags: ['experimental'] },
    { at: [16, 0], act: 'Machaki', tags: ['riddim'] },
    { at: [17, 0], act: 'Rzrkt', tags: ['tearout'] },
    { at: [18, 0], act: 'Funtcase', tags: ['tearout'], minutes: 60 },
    { at: [20, 0], act: 'Label Takeover', tags: [], note: 'label takeover', minutes: 120 },
  ],
  'stage-prehistoric': [
    { at: [20, 0], act: 'Mega b2b2b2b Pre-Party', tags: [], note: 'b2b2b2b', minutes: 120 },
  ],
};

const SATURDAY: StageDay = {
  'stage-prehistoric': [
    { at: [14, 0], act: 'Neotek', tags: ['riddim'] },
    { at: [15, 0], act: 'Capochino', tags: ['riddim'] },
    { at: [16, 0], act: 'Heyz', tags: ['tearout'] },
    { at: [16, 55], act: 'Effin', tags: ['riddim'] },
    { at: [17, 50], act: 'Jessica Audiffred', tags: ['tearout', 'trap'] },
    { at: [18, 50], act: 'Flux Pavilion', tags: ['tearout'] },
    { at: [19, 50], act: 'Zomboy', tags: ['tearout'] },
    { at: [20, 50], act: 'Kai Wachi', tags: ['tearout', 'riddim'] },
    { at: [21, 50], act: 'Slander', tags: ['melodic'] },
    { at: [22, 50], act: 'Illenium', tags: ['melodic'], minutes: 70 },
  ],
  'stage-wompy': [
    { at: [14, 30], act: 'All The Reason', tags: ['melodic'] },
    { at: [15, 30], act: 'HVDES', tags: ['hard', 'tearout'] },
    { at: [16, 30], act: 'Big Florida', tags: ['riddim'] },
    { at: [17, 30], act: 'PhaseOne', tags: ['tearout'] },
    { at: [18, 30], act: 'Ghengar', tags: ['riddim'] },
    { at: [19, 30], act: 'Seven Lions', tags: ['melodic'], note: 'sunset set' },
    { at: [20, 30], act: 'Zingara', tags: ['melodic', 'experimental'] },
    { at: [21, 30], act: 'Ganja White Night', tags: ['experimental'] },
    { at: [22, 45], act: 'Subtronics b2b Level Up', tags: ['riddim', 'tearout'], note: 'b2b' },
    { at: [0, 15], act: 'Ivory', tags: ['melodic'] },
    { at: [1, 10], act: 'Space Wizard', tags: ['experimental'] },
    { at: [2, 5], act: 'Tisoki', tags: ['tearout'] },
    { at: [3, 0], act: 'Brainrack', tags: ['tearout'], minutes: 60 },
  ],
  'stage-crater': [
    { at: [14, 0], act: '2DY4', tags: ['riddim'] },
    { at: [15, 15], act: 'Whales', tags: ['tearout'] },
    { at: [16, 25], act: 'Stoned Level', tags: ['riddim'] },
    { at: [17, 30], act: 'Prosecute', tags: ['riddim'] },
    { at: [18, 35], act: 'Cyclops', tags: ['riddim', 'tearout'] },
    { at: [19, 40], act: 'Layz', tags: ['tearout'] },
    { at: [20, 45], act: 'Whethan', tags: ['house', 'melodic'] },
    { at: [21, 50], act: 'Dr. Fresch', tags: ['house'] },
    { at: [22, 55], act: 'Flosstradamus', tags: ['trap'] },
    { at: [0, 0], act: 'Secret Takeover (midnight)', tags: [] },
    { at: [1, 0], act: 'Secret Takeover (1am)', tags: [] },
    { at: [2, 0], act: 'Secret Takeover (2am)', tags: [] },
    { at: [3, 0], act: 'Secret Takeover (3am)', tags: [], minutes: 60 },
  ],
  'stage-forest': [
    { at: [16, 0], act: 'Crumb Pit', tags: ['experimental'] },
    { at: [17, 0], act: 'Bella Renee', tags: ['dnb'] },
    { at: [18, 0], act: 'Mozey', tags: ['dnb'] },
    { at: [19, 0], act: 'Delta Heavy', tags: ['dnb'] },
    { at: [20, 0], act: 'Hedex', tags: ['dnb'] },
    { at: [21, 0], act: 'Bou', tags: ['dnb'] },
    { at: [22, 0], act: 'Mefjus + Daxta MC', tags: ['dnb'] },
    { at: [23, 0], act: 'AEON:MODE b2b Blossom', tags: ['dnb'], note: 'b2b' },
    { at: [0, 0], act: 'Reaper', tags: ['dnb'] },
    { at: [1, 0], act: 'Dirtyphonics', tags: ['dnb'] },
    { at: [2, 0], act: 'Craze b2b Dieselboy', tags: ['dnb'], note: 'b2b' },
    { at: [3, 0], act: 'Imanu', tags: ['dnb', 'experimental'], minutes: 60 },
  ],
  'stage-subsidia': [
    { at: [15, 0], act: 'Myrias', tags: ['riddim'] },
    { at: [16, 0], act: 'Green Matter', tags: ['riddim'] },
    { at: [17, 0], act: 'Saint Miller', tags: ['tearout'] },
    { at: [18, 0], act: 'Noetika', tags: ['melodic'] },
    { at: [19, 0], act: 'Chozen', tags: ['riddim'] },
    { at: [20, 0], act: 'Truth', tags: ['experimental'] },
    { at: [21, 0], act: 'Tokyo Machine', tags: ['tearout'] },
    { at: [22, 0], act: 'Leotrix', tags: ['tearout'] },
    { at: [23, 0], act: 'Au5', tags: ['melodic'] },
    { at: [0, 0], act: 'Mythm', tags: ['experimental'] },
    { at: [1, 0], act: 'Wraz', tags: ['riddim'] },
    { at: [2, 0], act: 'Josh Teed', tags: ['melodic', 'experimental'] },
    { at: [3, 0], act: 'Lowcation', tags: ['experimental'], minutes: 60 },
  ],
  'stage-raptor': [
    { at: [0, 0], act: 'Lil Texas', tags: ['hard'] },
    { at: [1, 0], act: 'Gladde Paling', tags: ['hard'] },
    { at: [2, 0], act: 'Darksiderz b2b Madgrrl', tags: ['hard'], note: 'b2b' },
    { at: [3, 0], act: 'Audiofreq', tags: ['hard'], minutes: 60 },
  ],
};

const SUNDAY: StageDay = {
  'stage-prehistoric': [
    { at: [14, 0], act: 'Usaybflow', tags: ['riddim'] },
    { at: [14, 55], act: 'Mad Dubz', tags: ['riddim'] },
    { at: [15, 50], act: 'Avello', tags: ['riddim'] },
    { at: [16, 45], act: 'Grabbitz', tags: ['melodic'] },
    { at: [17, 40], act: 'Kompany', tags: ['tearout'] },
    { at: [18, 35], act: 'Surprise Guest', tags: [] },
    { at: [19, 30], act: 'ATLiens', tags: ['trap'], note: 'sunset set' },
    { at: [20, 30], act: 'Alldat Crankdat b2b Alleycvt', tags: ['tearout'], note: 'b2b' },
    {
      at: [21, 40],
      act: 'Excision b2b Space Laces',
      tags: ['tearout', 'riddim'],
      note: 'closing ceremony',
      minutes: 80,
    },
  ],
  'stage-wompy': [
    { at: [15, 0], act: 'Dream Takers', tags: ['melodic'] },
    { at: [16, 0], act: 'Wonkywilla', tags: ['riddim'] },
    { at: [17, 0], act: 'Know Good', tags: ['experimental'] },
    { at: [18, 0], act: 'Eptic b2b Lyny', tags: ['tearout'], note: 'b2b' },
    { at: [19, 0], act: 'Excision', tags: ['experimental'], note: 'detox set' },
    { at: [19, 50], act: 'Adventure Club', tags: ['melodic'], note: 'throwback set' },
    { at: [20, 50], act: 'Virtual Riot', tags: ['tearout', 'melodic'] },
    { at: [21, 45], act: 'Boogie T', tags: ['tearout'] },
    { at: [23, 15], act: 'ARMNHMR', tags: ['melodic'] },
    { at: [0, 10], act: 'Haliene', tags: ['melodic'] },
    { at: [1, 5], act: 'Crystal Skies', tags: ['melodic'] },
    { at: [2, 0], act: 'Yetep', tags: ['melodic'], minutes: 60 },
  ],
  'stage-crater': [
    { at: [14, 0], act: 'Pretty Sweet', tags: ['riddim'] },
    { at: [15, 15], act: 'Luci', tags: ['riddim'] },
    { at: [16, 25], act: 'Codd Dubz', tags: ['riddim'] },
    { at: [17, 30], act: 'Trivecta', tags: ['melodic'] },
    { at: [18, 35], act: 'Sodown', tags: ['melodic', 'experimental'] },
    { at: [19, 40], act: 'William Black', tags: ['melodic'] },
    { at: [20, 45], act: 'Krewella', tags: ['melodic'] },
    { at: [21, 50], act: 'Champagne Drip', tags: ['experimental'] },
    { at: [23, 0], act: 'Secret Takeover (11pm)', tags: [] },
    { at: [0, 0], act: 'Secret Takeover (midnight)', tags: [] },
    { at: [1, 0], act: 'Secret Takeover (1am)', tags: [] },
    { at: [2, 0], act: 'Secret Takeover (2am)', tags: [], minutes: 60 },
  ],
  'stage-forest': [
    { at: [16, 0], act: 'Ryns', tags: ['dnb'] },
    { at: [17, 0], act: 'Distant Matter', tags: ['dnb'] },
    { at: [18, 0], act: 'Arlo', tags: ['dnb'] },
    { at: [19, 0], act: 'Zoey808', tags: ['dnb'] },
    { at: [20, 0], act: 'Taiki Nulight', tags: ['house'] },
    { at: [21, 0], act: 'Skilah', tags: ['house'] },
    { at: [22, 0], act: 'Passport', tags: ['house'] },
    { at: [23, 0], act: 'Wax Motif', tags: ['house'] },
    { at: [0, 0], act: 'Stumpi', tags: ['house'] },
    { at: [1, 0], act: 'Black Tiger Sex Machine', tags: ['tearout'], note: 'unmasked set' },
    { at: [2, 0], act: 'Ghastly', tags: ['tearout'], minutes: 60 },
  ],
  'stage-subsidia': [
    { at: [16, 0], act: 'Finnuh', tags: ['riddim'] },
    { at: [17, 0], act: 'Sqishi', tags: ['riddim'] },
    { at: [18, 0], act: 'Roi', tags: ['riddim'] },
    { at: [19, 0], act: 'Hurtbox', tags: ['tearout'] },
    { at: [20, 0], act: 'Hostage Situation', tags: ['tearout'] },
    { at: [21, 0], act: 'Modal Nodes', tags: ['experimental'] },
    { at: [22, 0], act: 'Warlord', tags: ['tearout'] },
    { at: [23, 0], act: 'YVM3', tags: ['riddim'] },
    { at: [0, 0], act: 'Sisto', tags: ['riddim'] },
    { at: [1, 0], act: 'OG Nixin', tags: ['riddim'] },
    { at: [2, 0], act: 'Onara', tags: ['tearout'], minutes: 60 },
  ],
  'stage-raptor': [
    { at: [23, 0], act: 'Sportmode', tags: ['hard'] },
    { at: [0, 0], act: 'Remk', tags: ['hard'] },
    { at: [1, 0], act: 'Killmatter', tags: ['hard'] },
    { at: [2, 0], act: 'Excision Presents Team Takeover', tags: ['hard'], minutes: 60 },
  ],
};

const SCHEDULE: Record<string, StageDay> = {
  'day-wed': WEDNESDAY,
  'day-thu': THURSDAY,
  'day-fri': FRIDAY,
  'day-sat': SATURDAY,
  'day-sun': SUNDAY,
};

function slug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// Built in the venue's zone rather than the machine's. The same bill loaded on
// a laptop set to another zone produced a different instant for every set.
function tsOf(calendarDate: string, at: [number, number], rolloverHour: number): number {
  const time = `${String(at[0]).padStart(2, '0')}:${String(at[1]).padStart(2, '0')}`;
  return festivalTimestamp(calendarDate, time, VENUE_TIMEZONE, rolloverHour);
}

const artistIndex = new Map<string, Artist>();

function artistFor(act: string, tags: string[]): Artist {
  const id = `artist-${slug(act)}`;
  const existing = artistIndex.get(id);
  if (existing) return existing;
  const artist: Artist = { id, name: act, genreTags: tags, aliases: [] };
  artistIndex.set(id, artist);
  return artist;
}

const builtSets: FestivalSet[] = [];

for (const [dayId, stageDay] of Object.entries(SCHEDULE)) {
  const day = DAYS.find((d) => d.id === dayId)!;
  for (const [stageId, slots] of Object.entries(stageDay)) {
    const ordered = [...slots].sort(
      (a, b) =>
        tsOf(day.calendarDate, a.at, day.rolloverHour) - tsOf(day.calendarDate, b.at, day.rolloverHour),
    );
    ordered.forEach((slot, i) => {
      const startTs = tsOf(day.calendarDate, slot.at, day.rolloverHour);
      const next = ordered[i + 1];
      const untilNext = next ? tsOf(day.calendarDate, next.at, day.rolloverHour) : Infinity;
      const declared = slot.minutes ? startTs + slot.minutes * 60 * 1000 : Infinity;
      // A set runs until the next one on its own stage, or for its declared length,
      // whichever ends first. One or the other is always finite.
      const endTs = Math.min(untilNext, declared, startTs + 60 * 60 * 1000 * 6);
      builtSets.push({
        id: `set-${dayId}-${stageId}-${slot.at[0]}-${slot.at[1]}`,
        artistId: artistFor(slot.act, slot.tags).id,
        stageId,
        dayId,
        startTs,
        endTs: Math.max(startTs + 15 * 60 * 1000, endTs),
        billingNote: slot.note,
      });
    });
  }
}

export const ARTISTS: Artist[] = [...artistIndex.values()];
export const SETS: FestivalSet[] = builtSets.sort((a, b) => a.startTs - b.startTs);
